import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReviewPage from '../../shared/booking/ReviewPage';
import { ExpiredSelection } from '../../shared/booking/FlowParts';
import { formatPrice } from '../../shared/data/common';
import { checkoutHotel } from '../../api/hotels';
import { useAuth } from '../../auth/AuthContext';
import HotelSummary from './HotelSummary';
import { freeCancellationText, penaltyLines } from './data';
import { fullAddress } from './HotelDetails';
import { nightsLabel } from './searchModel';

const checkoutStatus = { Confirmed: 'confirmed', Pending: 'pending' };

/** Booking summary saved with the booking and shown by HotelSummary. */
export function hotelSummary(review) {
  const { hotel, option } = review;
  const adults = review.rooms.reduce((n, r) => n + r.adults, 0);
  const children = review.rooms.reduce((n, r) => n + r.childAges.length, 0);
  return {
    hotelId: hotel.hotelId,
    supplierCode: hotel.supplierCode,
    name: hotel.name,
    image: hotel.imageUrl,
    stars: hotel.starRating,
    address: fullAddress(hotel.address),
    checkin: review.checkIn,
    checkout: review.checkOut,
    nights: review.nights,
    checkInTime: hotel.checkInFrom,
    checkOutTime: hotel.checkOutUntil,
    rooms: review.rooms.length,
    roomName: [...new Set(option.rooms.map((r) => r.name))].join(' + '),
    planName: option.mealBasis || 'Room only',
    adults,
    children,
    freeCancel: Boolean(freeCancellationText(option.cancellation)),
    freeCancellationUntil: option.cancellation.freeCancellationUntil,
    cancellation: option.cancellation,
  };
}

/**
 * One guest form per person, room by room: adults first (the first is the room's lead guest), then children.
 * PAN and passport fields appear only when the rate needs them; with `samePan` only the first guest enters a PAN.
 */
function guestSpecs(review, samePan) {
  const { panRequired, passportRequired } = review.option.compliance;
  const multi = review.rooms.length > 1;
  let index = 0;
  const documents = () => {
    const first = index === 0;
    index += 1;
    return [...(panRequired && (first || !samePan) ? ['pan'] : []), ...(passportRequired ? ['passport'] : [])];
  };
  return review.rooms.flatMap((room, i) => [
    ...Array.from({ length: room.adults }, (_, k) => ({
      type: 'Adult',
      room: i,
      label: `${multi ? `Room ${i + 1} · ` : ''}${k === 0 ? 'Lead guest' : `Adult ${k + 1}`}`,
      note: k === 0 ? 'Name the hotel will check you in under' : undefined,
      fields: ['title', 'firstName', 'lastName', ...documents()],
    })),
    ...room.childAges.map((age, k) => ({
      type: 'Child',
      room: i,
      label: `${multi ? `Room ${i + 1} · ` : ''}Child ${k + 1}`,
      note: `Age ${age}${panRequired && !samePan ? ' · enter a parent’s PAN' : ''}`,
      fields: ['title', 'firstName', 'lastName', ...documents()],
      dobRequired: false,
    })),
  ]);
}

export default function HotelReview() {
  const { state } = useLocation();
  const { auth, signInWith } = useAuth();
  const [samePan, setSamePan] = useState(false);
  if (!state?.review) return <ExpiredSelection module="hotels" />;

  const { review, backTo } = state;
  const { option } = review;
  const summary = hotelSummary(review);
  const specs = guestSpecs(review, samePan);
  const free = freeCancellationText(option.cancellation);
  const askSamePan = option.compliance.panRequired && specs.length > 1;

  const submit = async ({ travellers, contact, fare, payment, promoCode, paymentId }) => {
    // The supplier needs a PAN on every guest; with "same PAN" ticked, the first guest's is used for all.
    const panFor = (t) => {
      const pan = samePan && askSamePan ? travellers[0].pan : t.pan;
      return option.compliance.panRequired && pan ? pan.trim().toUpperCase() : null;
    };
    const rooms = review.rooms.map((_, i) => ({
      guests: travellers.filter((_, k) => specs[k].room === i).map((t) => ({
        type: t.type === 'Child' ? 'Child' : 'Adult',
        title: t.title,
        firstName: t.firstName.trim(),
        lastName: t.lastName.trim(),
        pan: panFor(t),
        passportNumber: t.passport ? t.passport.trim().toUpperCase() : null,
      })),
    }));

    let result;
    try {
      result = await checkoutHotel({
        reviewId: review.reviewId,
        contact: { email: contact.email.trim(), phone: contact.phone.trim() },
        rooms,
        gst: contact.gst ? { number: contact.gstin.trim().toUpperCase(), registeredName: contact.company.trim() } : null,
        promoCode,
        // The API stops before booking if its fee or discount no longer gives this total.
        expectedTotal: fare.total,
        paymentMethod: payment,
        paymentId,
      }, auth?.accessToken);
    } catch (error) {
      if (error.status === 409 && /expired/i.test(error.message)) {
        throw new Error('This room price has expired. Go back to the hotel and choose the room again to see the latest price.');
      }
      throw error;
    }

    if (result.status === 'Failed') {
      throw new Error("The hotel couldn't confirm this booking, so you haven't been charged. Please choose another room or hotel.");
    }
    if (result.auth) signInWith(result.auth);
    return {
      status: checkoutStatus[result.status] || 'pending',
      ref: result.confirmationNumber || result.supplierBookingId,
      supplierCode: review.hotel.supplierCode,
      supplierBookingId: result.supplierBookingId,
      account: { status: result.accountStatus, email: result.accountEmail, emailSent: result.emailSent },
    };
  };

  const contactNote = (
    <p className="muted small form-note account-note">
      The hotel may contact you on this number about your stay.
      {!auth?.accessToken && (
        <> Have a Trawtel account? <Link className="link small" to={`/account/login?returnTo=${encodeURIComponent(backTo)}`}>Sign in</Link> first,
          or we'll add this booking to the account for this email — and create one if it doesn't exist yet.</>
      )}
    </p>
  );

  const { price } = option;
  return (
    <ReviewPage
      module="hotels"
      heading="Review your stay"
      back={{ to: backTo, label: 'Change room' }}
      summary={<HotelSummary summary={summary} />}
      info={[
        ...(review.priceChanged ? [`The hotel's price changed from ${formatPrice(review.previousRoomPrice)} to ${formatPrice(price.roomPrice)} since you chose this room. The total below is the new price.`] : []),
        summary.checkInTime ? `Check-in from ${summary.checkInTime}${summary.checkOutTime ? `; check-out until ${summary.checkOutTime}` : ''}.` : 'Check-in and check-out times are shown on your voucher.',
        'Every guest must carry a valid government photo ID. Enter names exactly as on the ID.',
        ...(option.compliance.panRequired ? ['This rate needs a PAN (Indian tax rules); the hotel supplier verifies it. One PAN can be used for all guests.'] : []),
        ...(option.compliance.passportRequired ? ['This hotel needs each guest’s passport number.'] : []),
        free ? `${free}.` : 'This rate is non-refundable.',
        ...penaltyLines(option.cancellation, formatPrice),
        ...(option.bookingNotes ? [option.bookingNotes] : []),
      ]}
      travellers={specs}
      travellersIntro={askSamePan && (
        <label className="check same-pan">
          <input type="checkbox" checked={samePan} onChange={(e) => setSamePan(e.target.checked)} />
          <span>Use the same PAN for all guests <small className="muted">— enter it once for the first guest</small></span>
        </label>
      )}
      travelDate={review.checkIn}
      travelEnd={review.checkOut}
      fareLines={[
        [`Room charges · ${review.rooms.length} room${review.rooms.length > 1 ? 's' : ''} × ${nightsLabel(review.nights)}`, price.baseFare],
        ...(price.taxes > 0 ? [['Taxes', price.taxes, 'tax']] : []),
        ...(price.managementFee > 0 ? [['Management fee (incl. tax)', price.managementFee, 'tax']] : []),
        ...(price.discount > 0 ? [['Hotel discount', -price.discount]] : []),
      ]}
      onSubmit={submit}
      submitNotice="The room is booked with the hotel on confirmation."
      initialContact={auth?.user?.email ? { email: auth.user.email } : undefined}
      contactNote={contactNote}
      buildBooking={() => ({
        title: summary.name,
        subtitle: `${summary.roomName} · ${nightsLabel(summary.nights)} · ${summary.rooms} room${summary.rooms > 1 ? 's' : ''}`,
        travelDate: summary.checkin,
        endDate: summary.checkout,
        summary,
      })}
    />
  );
}
